import {
    serverTimestamp,
    doc,
    updateDoc,
    addDoc,
    setDoc,
    collection,
    getDocs,
    query,
    orderBy,
    deleteDoc,
    increment,
} from 'firebase/firestore';

import { EvaluationShareOptions } from '@labradorsports/constants';
import { calculateEvaluationScore, processEvaluation, safeToDate } from '@labradorsports/utils';

import firestore from '../../firebase-config.js';
import { DataTags } from '../../shared/constants.js';
import { createApiSlice } from '../api.js';
import { routerActions } from '../router/index.js';
import { CustomMutationDefinition, CustomQueryDefinition } from '../types.js';

function prepRating(evaluationSrc) {
    const logIfBad = () => null;

    return processEvaluation({ ...evaluationSrc }, logIfBad);
}

function prepTemplateSnap(templateSnap) {
    const template = templateSnap.data();
    template.id = templateSnap.id;
    template.createdDate = safeToDate(template.createdDate);
    template.lastModified = safeToDate(template.lastModified);

    return template;
}

function prepTemplateRevisions(revisions: { [key: string]: any }) {
    return Object.fromEntries(
        Object.entries(revisions).map(([key, revision]) => [
            key,
            {
                ...prepRating(revision),
                id: revision.templateId,
            },
        ])
    );
}

export const evaluationsApi = createApiSlice({
    saveEvaluation: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['player', 'evaluation'],
        },
        queryFn: async ({ activeTeam, evaluation, player, ratingId, template }, { dispatch, extra }) => {
            const { logger } = extra;
            const playerRef = doc(firestore, 'teams', activeTeam, 'roster', player.id);

            const overallRating = calculateEvaluationScore(
                template.positionWeights,
                player.position,
                evaluation.scores,
                template.scoreScale
            );

            const fullEvaluation = {
                ...evaluation,
                overallRating,
                lastModified: serverTimestamp(),
            };

            let ratingsRef;
            if (ratingId) {
                // Update existing rating
                ratingsRef = doc(collection(playerRef, 'evaluations'), ratingId);
                await setDoc(ratingsRef, fullEvaluation, { merge: true });
            } else {
                // Create new rating
                ratingsRef = await addDoc(collection(playerRef, 'evaluations'), {
                    ...fullEvaluation,
                    templateId: template.id,
                    templateVersion: template.version,
                    createdDate: serverTimestamp(),
                });
            }

            logger.log('saveEvaluation rating add/update complete');

            dispatch(routerActions.SetPlayerEvaluationId(ratingId || ratingsRef.id));

            logger.log('saveEvaluation update player');

            await updateDoc(playerRef, {
                overallRating,
            });

            return {
                data: { id: ratingId ?? ratingsRef.id },
            };
        },
        invalidatesTags: (result, error, { player, ratingId }) => {
            return [
                { type: DataTags.ROSTER_PERSON, id: player.id },
                { type: DataTags.EVALUATION_TEMPLATE_REVISION, id: 'LIST' },
                ratingId
                    ? { type: DataTags.PLAYER_RATING, id: `${player.id}_${ratingId}` }
                    : { type: DataTags.PLAYER_RATING, id: `${player.id}_LIST` },
            ];
        },
    } as CustomMutationDefinition<
        {
            activeTeam: string;
            evaluation: any;
            player: any;
            ratingId?: string;
            template: EvaluationTemplate;
        },
        any
    >,

    saveEvaluationTemplate: {
        type: 'mutation',
        queryFn: async ({ activeTeam, template, id }, { extra }) => {
            const { logger } = extra;
            const templatesRef = collection(firestore, 'teams', activeTeam, 'evaluationTemplates');
            const { createdDate, lastModified, ...rest } = template;

            let savedId = id;
            if (id) {
                // Update existing template
                const templateDocRef = doc(templatesRef, id);
                await setDoc(
                    templateDocRef,
                    {
                        ...rest,
                        lastModified: serverTimestamp(),
                        version: increment(1),
                    },
                    { merge: true }
                );
            } else {
                // Create new template
                const added = await addDoc(templatesRef, {
                    ...rest,
                    createdDate: serverTimestamp(),
                    lastModified: serverTimestamp(),
                    version: 1,
                });

                savedId = added.id;
            }

            logger.log('saveEvaluationTemplate complete');

            return {
                data: savedId,
            };
        },
        invalidatesTags: () => {
            return [{ type: DataTags.EVALUATION_TEMPLATES, id: 'LIST' }];
        },
    } as CustomMutationDefinition<
        { activeTeam: string; template: Omit<EvaluationTemplate, 'id' | 'version'>; id?: string },
        any
    >,

    deleteEvaluationTemplate: {
        type: 'mutation',
        queryFn: async ({ activeTeam, templateId }, { extra }) => {
            const { logger } = extra;
            await deleteDoc(doc(firestore, 'teams', activeTeam, 'evaluationTemplates', templateId));
            logger.log('deleteEvaluationTemplate complete');
        },
        invalidatesTags: (result, error, arg) => [
            { type: DataTags.EVALUATION_TEMPLATES, id: 'LIST' },
            { type: DataTags.EVALUATION_TEMPLATES, id: arg.templateId },
        ],
    } as CustomMutationDefinition<{ activeTeam: string; templateId: string }, any>,

    getPlayerEvaluations: {
        type: 'query',
        query: ({ activeTeam, playerId }) => ({
            path: 'teams/getPlayerEvaluations',
            query: { teamId: activeTeam, rosterId: playerId },
        }),
        transformResponse: (response: any) => {
            return {
                evaluations: response.evaluations.map(prepRating),
                templateRevisions: prepTemplateRevisions(response.templateRevisions),
            };
        },
        providesTags: ({ evaluations, templateRevisions }, error, { playerId }) => {
            return [
                { type: DataTags.PLAYER_RATING, id: `${playerId}_LIST` },
                { type: DataTags.EVALUATION_TEMPLATE_REVISION, id: 'LIST' },
                ...evaluations.map(({ id }) => ({ type: DataTags.PLAYER_RATING, id: `${playerId}_${id}` })),
                ...Object.keys(templateRevisions).map((id) => ({ type: DataTags.EVALUATION_TEMPLATE_REVISION, id })),
            ];
        },
    } as CustomQueryDefinition<{ activeTeam: string; playerId: string }, any>,

    getEvaluationTemplates: {
        type: 'query',
        queryFn: async ({ activeTeam }) => {
            const templatesRef = collection(firestore, 'teams', activeTeam, 'evaluationTemplates');
            const snapshot = await getDocs(query(templatesRef, orderBy('createdDate', 'desc')));

            return {
                data: snapshot.docs.map(prepTemplateSnap),
            };
        },
        providesTags: (templates = []) => [
            { type: DataTags.EVALUATION_TEMPLATES, id: 'LIST' },
            ...templates.map(({ id }) => ({ type: DataTags.EVALUATION_TEMPLATES, id })),
        ],
    } as CustomQueryDefinition<{ activeTeam: string }, any>,

    deleteEvaluation: {
        type: 'mutation',
        queryFn: async ({ activeTeam, playerId, evaluationId }, { extra }) => {
            const { logger } = extra;
            const playerRef = doc(firestore, 'teams', activeTeam, 'roster', playerId);

            await deleteDoc(doc(collection(playerRef, 'evaluations'), evaluationId));

            logger.log('deleteEvaluation complete');
        },
        invalidatesTags: (result, error, { playerId, evaluationId }) => [
            { type: DataTags.PLAYER_RATING, id: `${playerId}_LIST` },
            { type: DataTags.PLAYER_RATING, id: `${playerId}_${evaluationId}` },
        ],
    } as CustomMutationDefinition<{ activeTeam: string; playerId: string; evaluationId: string }, any>,

    shareEvaluation: {
        type: 'mutation',
        query: ({ activeTeam, rosterId, evaluationId, shareType }) => ({
            path: 'teams/shareEvaluation',
            body: {
                teamId: activeTeam,
                rosterId,
                evaluationId,
                shareType,
            },
        }),
        invalidatesTags: (result, error, { rosterId, evaluationId }) => [
            { type: DataTags.PLAYER_RATING, id: `${rosterId}_LIST` },
            { type: DataTags.PLAYER_RATING, id: `${rosterId}_${evaluationId}` },
        ],
    } as CustomMutationDefinition<
        {
            activeTeam: string;
            rosterId: string;
            evaluationId: string;
            shareType: keyof typeof EvaluationShareOptions;
        },
        any
    >,

    getMyEvaluations: {
        type: 'query',
        query: () => ({
            path: 'teams/getMyEvaluations',
        }),
        transformResponse: (response: any) => {
            return {
                evaluations: response.evaluations.map(prepRating),
                templateRevisions: prepTemplateRevisions(response.templateRevisions),
            };
        },
        providesTags: ({ evaluations, templateRevisions }) => {
            return [
                { type: DataTags.PLAYER_RATING, id: `MY_LIST` },
                ...evaluations.map(({ id }) => ({ type: DataTags.PLAYER_RATING, id: `MY_${id}` })),
                ...Object.keys(templateRevisions).map((id) => ({ type: DataTags.EVALUATION_TEMPLATE_REVISION, id })),
            ];
        },
    } as CustomQueryDefinition<void, any>,
});

export const {
    useGetPlayerEvaluationsQuery,
    useSaveEvaluationMutation,
    useGetEvaluationTemplatesQuery,
    useSaveEvaluationTemplateMutation,
    useDeleteEvaluationTemplateMutation,
    useDeleteEvaluationMutation,
    useShareEvaluationMutation,
    useGetMyEvaluationsQuery,
} = evaluationsApi;
